import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useWindowActions } from '../../../../models/window'
import { routes } from 'core/routes/paths'
import { formatNumbers } from 'core/helpers/format-numbers'
import { toast } from 'react-toastify'
import SheetModal from '../sheet-modal/sheet-modal'
import medalGold from 'features/leaderboard/table/item/assets/images/medal-gold.png'
import medalSilver from 'features/leaderboard/table/item/assets/images/medal-silver.png'
import medalBronze from 'features/leaderboard/table/item/assets/images/medal-bronze.png'
import coinIcon from 'features/leaderboard/tabs/assets/images/coin.svg'
import energyIcon from 'features/leaderboard/tabs/assets/images/energy.png'
import leaderboardIcon from 'features/tabs/assets/images/leaderboard-icon.png'
import c from './leaderboard-reward-modal.module.scss'

const MEDALS = { 1: medalGold, 2: medalSilver, 3: medalBronze }

const LeaderboardRewardModal = ({ entryId, type, rank, boardType, amount, caseId, caseName, caseImage, promoCode, description, imageUrl }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { closeWindow } = useWindowActions()
    const [copied, setCopied] = useState(false)
    const medal = MEDALS[rank]

    const handleCopy = () => {
        if (!promoCode) return
        navigator.clipboard.writeText(promoCode).then(() => {
            toast(t('profile.cases.error.codeCopied'), { type: 'success' })
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        }).catch(() => {
            toast(t('profile.cases.error.copyError'), { type: 'error' })
        })
    }

    const handleAction = async () => {
        if (entryId) {
            try {
                const { apiClient } = await import('../../../../core/http')
                await apiClient.post(`/leaderboard/reward/${entryId}/seen`)
            } catch (_) {}
        }
        closeWindow()
        if (type === 'COINS' || type === 'CASE') {
            navigate(routes.profile)
        }
    }

    const renderBadge = () => {
        if (medal) {
            return <img className={c.medalImg} src={medal} alt={`${rank} место`} />
        }
        return (
            <div className={c.badgeWrap}>
                <img className={c.badgeIcon} src={leaderboardIcon} alt="" />
                <div className={c.badgeNumber}>{rank}</div>
            </div>
        )
    }

    const getRewardContent = () => {
        switch (type) {
            case 'COINS':
                return (
                    <div className={c.rewardBlock}>
                        <div className={c.rewardTitle}>
                            {t('pages.leaderboard.rewardCoins', 'Монеты')}
                        </div>
                        <div className={c.rewardValue}>
                            {formatNumbers(amount || 0)}
                            <img className={c.inlineIconLg} src={coinIcon} alt="" />
                        </div>
                    </div>
                )
            case 'ENERGY':
                return (
                    <div className={c.rewardBlock}>
                        <div className={c.rewardTitle}>
                            {t('pages.leaderboard.rewardEnergy', 'Энергия')}
                        </div>
                        <div className={c.rewardValue}>
                            +{formatNumbers(amount || 0)}
                            <img className={c.inlineIconLg} src={energyIcon} alt="" />
                        </div>
                    </div>
                )
            case 'CASE':
                return (
                    <div className={c.rewardBlock}>
                        <div className={c.rewardTitle}>
                            {t('pages.leaderboard.rewardCase', 'Бесплатный кейс')}
                        </div>
                        {caseImage && <img className={c.rewardImage} src={caseImage} alt="" />}
                        <div className={c.rewardValue}>{caseName || 'Кейс'}</div>
                    </div>
                )
            case 'PROMO_CODE':
            case 'PROMO_REUSABLE':
                return (
                    <div className={c.rewardBlock}>
                        <div className={c.rewardTitle}>
                            {t('pages.leaderboard.rewardPromo', 'Промокод')}
                        </div>
                        {promoCode && (
                            <div className={c.promoCode}>
                                <span className={c.codeText}>{promoCode}</span>
                                <span className={c.codeLabel}>{description || t('profile.cases.promoCode')}</span>
                                <button className={c.copyBtn} onClick={handleCopy}>
                                    {copied ? t('profile.cases.copied') : t('profile.cases.copy')}
                                </button>
                            </div>
                        )}
                    </div>
                )
            case 'CUSTOM':
                return (
                    <div className={c.rewardBlock}>
                        {imageUrl && <img className={c.rewardImage} src={imageUrl} alt="" />}
                        <div className={c.rewardTitle}>{description || 'Награда'}</div>
                    </div>
                )
            default:
                return null
        }
    }

    const getButtonText = () => {
        if (type === 'COINS') return t('pages.leaderboard.spendReward', 'Потратить')
        if (type === 'CASE') return t('pages.leaderboard.claimReward', 'Забрать награду')
        return t('pages.leaderboard.closeReward', 'Закрыть')
    }

    return (
        <SheetModal>
            <div className={c.component}>
                {renderBadge()}
                <h3 className={c.title}>
                    {t('pages.leaderboard.rewardTitle', 'Ты занял {{rank}} место!', { rank })}
                </h3>
                <p className={c.subtitle}>
                    {boardType === 'REFERRALS'
                        ? t('pages.leaderboard.rewardSubtitleReferrals', 'В лидерборде по рефералам')
                        : t('pages.leaderboard.rewardSubtitleCoins', 'В лидерборде по монетам')}
                </p>
                {getRewardContent()}
                <button className={c.button} onClick={handleAction}>
                    {getButtonText()}
                </button>
            </div>
        </SheetModal>
    )
}

export default LeaderboardRewardModal
